import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatINRShort } from "../lib/api";
import { usePolling } from "../lib/usePolling";
import { Workflow, Plus, FileText, Receipt, ScrollText, Truck, Users, MapPin, X, Trash2, Link2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import DispatchPanel from "./DispatchPanel";
import { LifecycleBadge } from "../components/LifecycleBadge";
import { DirectoryPicker } from "../components/DirectoryPicker";

const PHASES = {
  sales: { label: "Sales", color: "#8A6D1B", bg: "#F7F1DE" },
  ops: { label: "Operations", color: "#2B5FB3", bg: "#EAF0FB" },
  billing: { label: "Billing", color: "#2F6F4E", bg: "#E3F0E7" },
};

export default function Consignments() {
  const [stages, setStages] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("pipeline");
  const [niOnly, setNiOnly] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([api.get("/consignments/stages"), api.get("/consignments")]);
      setStages(s.data || []);
      setItems(c.data || []);
    } catch { toast.error("Could not load consignments"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  usePolling(load, 20000, !dragId && !detail && !showNew && !showQuote);

  async function moveTo(c, stageKey) {
    if (c.stage === stageKey) return;
    const prev = items;
    setItems((xs) => xs.map((x) => x.id === c.id ? { ...x, stage: stageKey } : x));
    try { await api.patch(`/consignments/${c.id}`, { stage: stageKey }); }
    catch { setItems(prev); toast.error("Could not move"); }
  }

  const byStage = useMemo(() => {
    const m = {};
    (stages || []).forEach((s) => { m[s.key] = []; });
    (items || []).filter((c) => !niOnly || c.needs_invoice).forEach((c) => { (m[c.stage] || (m[c.stage] = [])).push(c); });
    return m;
  }, [stages, items, niOnly]);

  const stats = useMemo(() => {
    const open = items.filter((c) => (c.outcome || "open") === "open");
    const won = items.filter((c) => c.outcome === "won");
    const lost = items.filter((c) => c.outcome === "lost");
    const decided = won.length + lost.length;
    const sum = (arr) => arr.reduce((a, c) => a + (Number(c.value) || 0), 0);
    return {
      total: items.length, open: open.length, won: won.length, lost: lost.length,
      winRate: decided ? Math.round((won.length / decided) * 100) : 0,
      openValue: sum(open), wonValue: sum(won),
      needsInvoice: items.filter((c) => c.needs_invoice).length,
    };
  }, [items]);

  return (
    <div data-testid="consignments-page">
      <div className="flex items-center gap-1 border-b border-[#E5E3DB] mb-6">
        <button onClick={() => setTab("pipeline")} data-testid="tab-pipeline"
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "pipeline" ? "border-[#C84B31] text-[#1A1A18]" : "border-transparent text-[#68665E] hover:text-[#1A1A18]"}`}>
          <Workflow className="w-4 h-4 inline mr-1.5" /> Pipeline
        </button>
        <button onClick={() => setTab("dispatch")} data-testid="tab-dispatch"
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "dispatch" ? "border-[#C84B31] text-[#1A1A18]" : "border-transparent text-[#68665E] hover:text-[#1A1A18]"}`}>
          <Truck className="w-4 h-4 inline mr-1.5" /> EV Dispatch
        </button>
      </div>

      {tab === "dispatch" && <DispatchPanel />}
      {tab === "pipeline" && (
        <>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">Pipeline</div>
          <h1 className="font-headings text-4xl font-bold text-[#1A1A18] mt-1 flex items-center gap-2"><Workflow className="w-8 h-8 text-[#C84B31]" /> Consignments</h1>
          <p className="text-sm text-[#68665E] mt-1">Every deal from inquiry → quote → operations → billing. Drag a card to change stage; it also advances automatically as quotes, LRs & invoices update.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowQuote(true)} className="btn-secondary" data-testid="consignment-from-quote-btn"><ScrollText className="w-4 h-4" /> From Quotation</button>
          <button onClick={() => setShowNew(true)} className="btn-primary" data-testid="consignment-new-btn"><Plus className="w-4 h-4" /> New Inquiry</button>
        </div>
      </div>

      {loading ? <div className="text-sm text-[#68665E]">Loading…</div> : (
        <>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5" data-testid="pipeline-stats">
          <StatTile label="Open deals" value={stats.open} />
          <StatTile label="Pipeline value" value={formatINRShort(stats.openValue)} accent="#2B5FB3" />
          <StatTile label="Won" value={stats.won} accent="#2F6F4E" />
          <StatTile label="Won value" value={formatINRShort(stats.wonValue)} accent="#2F6F4E" />
          <StatTile label="Lost" value={stats.lost} accent="#8A2526" />
          <StatTile label="Win rate" value={`${stats.winRate}%`} accent="#C84B31" testId="pipeline-winrate" />
        </div>
        {(stats.needsInvoice > 0 || niOnly) && (
          <div className="mb-4">
            <button
              onClick={() => setNiOnly((v) => !v)}
              data-testid="needs-invoice-filter"
              className={`inline-flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-full border transition-colors ${niOnly ? "bg-[#8A5A0B] text-white border-[#8A5A0B]" : "bg-[#FBEAD1] text-[#8A5A0B] border-[#E8C98A] hover:bg-[#F8E0BE]"}`}>
              <AlertTriangle className="w-4 h-4" />
              <span data-testid="needs-invoice-count">{stats.needsInvoice}</span> need{stats.needsInvoice === 1 ? "s" : ""} invoice
              <span className="opacity-80">· {niOnly ? "showing only these — clear" : "show only"}</span>
            </button>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="consignments-board">
          {stages.filter((s) => s.key !== "payment_received").map((s) => {
            const ph = PHASES[s.phase] || PHASES.sales;
            const cards = byStage[s.key] || [];
            const stageValue = cards.filter((c) => c.outcome !== "lost").reduce((a, c) => a + (Number(c.value) || 0), 0);
            return (
              <div key={s.key}
                onDragOver={(e) => { e.preventDefault(); setOverCol(s.key); }}
                onDragLeave={() => setOverCol((c) => (c === s.key ? null : c))}
                onDrop={(e) => { e.preventDefault(); setOverCol(null); const c = items.find((x) => x.id === dragId); setDragId(null); if (c) moveTo(c, s.key); }}
                data-testid={`consignment-col-${s.key}`}>
                <div className="rounded-t-lg px-3 py-2 flex items-center justify-between gap-2" style={{ background: ph.bg }}>
                  <span className="text-xs font-bold truncate" style={{ color: ph.color }}>{s.label}</span>
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-white/70 whitespace-nowrap" style={{ color: ph.color }} data-testid={`consignment-count-${s.key}`}>{cards.length} · {formatINRShort(stageValue)}</span>
                </div>
                <div className={`rounded-b-lg border border-t-0 p-2 space-y-2 min-h-[140px] ${overCol === s.key ? "border-[#C84B31] bg-[#FBEEEA]" : "border-[#E5E3DB] bg-[#F7F6F2]"}`}>
                  {cards.length === 0 && <div className="text-[11px] text-[#A09E96] text-center py-4">—</div>}
                  {cards.map((c) => (
                    <div key={c.id} draggable onDragStart={() => setDragId(c.id)} onDragEnd={() => { setDragId(null); setOverCol(null); }}
                      onClick={() => setDetail(c)} data-testid={`consignment-card-${c.id}`}
                      className={`bg-white border border-[#E5E3DB] rounded-md p-2.5 shadow-sm cursor-pointer hover:border-[#C84B31] transition-colors ${dragId === c.id ? "opacity-50" : ""} ${c.outcome === "lost" ? "opacity-60" : ""}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-[11px] text-[#2B5FB3] font-semibold truncate">{(c.lr_numbers && c.lr_numbers.length) ? c.lr_numbers.join(", ") : "No LR yet"}</span>
                        <div className="flex items-center gap-1">
                          {c.outcome === "won" && <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-[#E3F0E7] text-[#2F6F4E]" data-testid={`badge-won-${c.id}`}>WON</span>}
                          {c.outcome === "lost" && <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-[#F6E0E0] text-[#8A2526]" data-testid={`badge-lost-${c.id}`}>LOST</span>}
                          {c.vehicle_mode === "own_fleet" ? <Truck className="w-3.5 h-3.5 text-[#68665E]" title="Own fleet" />
                            : c.vehicle_mode === "broker" ? <Users className="w-3.5 h-3.5 text-[#68665E]" title="Broker" /> : null}
                        </div>
                      </div>
                      <div className="text-sm font-medium text-[#1A1A18] mt-0.5 truncate">{c.customer_name || c.title || "Untitled"}</div>
                      {(c.from_location || c.to_location) && <div className="text-[11px] text-[#68665E] mt-0.5 flex items-center gap-1 truncate"><MapPin className="w-3 h-3 shrink-0" />{c.from_location} → {c.to_location}</div>}
                      {c.lifecycle && <div className="mt-1.5"><LifecycleBadge lifecycle={c.lifecycle} /></div>}
                      {c.needs_invoice && (
                        <div className="mt-1.5">
                          <span className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#FBEAD1] text-[#8A5A0B] border border-[#E8C98A]" data-testid={`badge-needs-invoice-${c.id}`}>
                            <AlertTriangle className="w-3 h-3" /> Needs invoice
                          </span>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {c.quotation_number && <span className="text-[9px] px-1 py-0.5 rounded bg-[#F7F1DE] text-[#8A6D1B]">{c.quotation_number}</span>}
                        {c.invoice_number && <span className="text-[9px] px-1 py-0.5 rounded bg-[#E3F0E7] text-[#2F6F4E]">{c.invoice_number}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}

      {showNew && <NewInquiryModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {showQuote && <FromQuoteModal onClose={() => setShowQuote(false)} onSaved={() => { setShowQuote(false); load(); }} />}
      {detail && <DetailModal c={detail} stages={stages} onClose={() => setDetail(null)} onChanged={(c) => { setDetail(c); load(); }} onDeleted={() => { setDetail(null); load(); }} />}
        </>
      )}
    </div>
  );
}

function Field({ label, ...p }) {
  return (<label className="block"><span className="text-xs text-[#68665E]">{label}</span><input className="input-field !py-1.5 text-sm mt-1" {...p} /></label>);
}

function StatTile({ label, value, accent = "#1A1A18", testId }) {
  return (
    <div className="bg-white border border-[#E5E3DB] rounded-lg px-3 py-2" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide text-[#68665E]">{label}</div>
      <div className="font-headings text-xl font-bold mt-0.5" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function NewInquiryModal({ onClose, onSaved }) {
  const [f, setF] = useState({ source: "email", customer_name: "", contact_phone: "", contact_email: "", from_location: "", to_location: "", cargo: "", vehicle_mode: "", notes: "" });
  const [customers, setCustomers] = useState([]);
  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {}); }, []);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  async function save() {
    if (!f.customer_name.trim()) { toast.error("Customer name required"); return; }
    try { await api.post("/consignments", f); toast.success("Inquiry logged"); onSaved(); }
    catch { toast.error("Could not create"); }
  }
  return (
    <Modal title="New Inquiry" onClose={onClose} testId="new-inquiry-modal">
      <div className="grid grid-cols-2 gap-3">
        <label className="block"><span className="text-xs text-[#68665E]">Source</span>
          <select className="input-field !py-1.5 text-sm mt-1" value={f.source} onChange={(e) => set("source", e.target.value)} data-testid="inquiry-source">
            <option value="email">Email</option><option value="call">Call</option><option value="other">Other</option>
          </select></label>
        <label className="block"><span className="text-xs text-[#68665E]">Vehicle</span>
          <select className="input-field !py-1.5 text-sm mt-1" value={f.vehicle_mode} onChange={(e) => set("vehicle_mode", e.target.value)} data-testid="inquiry-vehicle">
            <option value="">— TBD —</option><option value="broker">Broker</option><option value="own_fleet">Own fleet</option>
          </select></label>
        <div className="col-span-2"><span className="text-xs text-[#68665E]">Customer (from directory — auto-fills phone, email &amp; pickup address)</span>
          <DirectoryPicker testPrefix="inquiry-customer" kind="pickup"
            value={{ company: f.customer_name, address: f.from_location, poc_phone: f.contact_phone, poc_email: f.contact_email }}
            onSelect={(p) => setF((s) => ({ ...s,
              ...(p.company !== undefined ? { customer_name: p.company } : {}),
              ...(p.address !== undefined ? { from_location: p.address } : {}),
              ...(p.poc_phone !== undefined ? { contact_phone: p.poc_phone } : {}),
              ...(p.poc_email !== undefined ? { contact_email: p.poc_email } : {}),
            }))}
          />
        </div>
        <Field label="Contact phone" value={f.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} />
        <Field label="From" value={f.from_location} onChange={(e) => set("from_location", e.target.value)} />
        <Field label="To" value={f.to_location} onChange={(e) => set("to_location", e.target.value)} />
        <div className="col-span-2"><Field label="Cargo" value={f.cargo} onChange={(e) => set("cargo", e.target.value)} /></div>
      </div>
      <button onClick={save} className="btn-primary w-full mt-4" data-testid="inquiry-save">Log Inquiry</button>
    </Modal>
  );
}

function FromQuoteModal({ onClose, onSaved }) {
  const [quotes, setQuotes] = useState([]);
  useEffect(() => { api.get("/quotations").then((r) => setQuotes(r.data || [])).catch(() => {}); }, []);
  async function pick(q) {
    try { await api.post("/consignments", { quotation_id: q.id }); toast.success(`Deal created from ${q.quote_number}`); onSaved(); }
    catch { toast.error("Could not create"); }
  }
  return (
    <Modal title="Start from Quotation" onClose={onClose} testId="from-quote-modal">
      <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
        {quotes.length === 0 ? <div className="text-sm text-[#68665E] py-6 text-center">No quotations found.</div> :
          quotes.map((q) => (
            <button key={q.id} onClick={() => pick(q)} className="w-full text-left px-3 py-2 rounded border border-[#E5E3DB] hover:border-[#C84B31] hover:bg-[#FBEEEA]" data-testid={`quote-pick-${q.id}`}>
              <div className="flex items-center justify-between"><span className="font-mono text-xs text-[#C84B31]">{q.quote_number}</span><span className="text-[10px] text-[#68665E] uppercase">{q.status}</span></div>
              <div className="text-sm text-[#1A1A18]">{q.customer_name || "—"}</div>
              <div className="text-[11px] text-[#68665E]">{q.from_location} → {q.to_location}</div>
            </button>
          ))}
      </div>
    </Modal>
  );
}

function DetailModal({ c, stages, onClose, onChanged, onDeleted }) {
  const [link, setLink] = useState({ lr_number: "", invoice_number: "", quotation_number: "" });
  const [notes, setNotes] = useState(c.notes || "");
  const [showLost, setShowLost] = useState(false);
  const [lostReason, setLostReason] = useState(c.lost_reason || "");
  async function patch(body) {
    try { const { data } = await api.patch(`/consignments/${c.id}`, body); onChanged(data); }
    catch { toast.error("Update failed"); }
  }
  async function doLink() {
    if (!link.lr_number && !link.invoice_number && !link.quotation_number) return;
    try { const { data } = await api.post(`/consignments/${c.id}/link`, link); setLink({ lr_number: "", invoice_number: "", quotation_number: "" }); onChanged(data); toast.success("Linked"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Link failed"); }
  }
  async function remove() {
    if (!window.confirm(`Delete consignment for ${c.customer_name || (c.lr_numbers && c.lr_numbers[0]) || "this deal"}?`)) return;
    try { await api.delete(`/consignments/${c.id}`); toast.success("Deleted"); onDeleted(); }
    catch { toast.error("Delete failed"); }
  }
  return (
    <Modal title={`${(c.lr_numbers && c.lr_numbers.length) ? c.lr_numbers.join(", ") + " · " : ""}${c.customer_name || "Consignment"}`} onClose={onClose} testId="consignment-detail-modal">
      <div className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className="text-xs text-[#68665E]">Stage</span>
            <select className="input-field !py-1.5 text-sm mt-1" value={c.stage} onChange={(e) => patch({ stage: e.target.value })} data-testid="detail-stage">
              {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select></label>
          <label className="block"><span className="text-xs text-[#68665E]">Vehicle</span>
            <select className="input-field !py-1.5 text-sm mt-1" value={c.vehicle_mode || ""} onChange={(e) => patch({ vehicle_mode: e.target.value })} data-testid="detail-vehicle">
              <option value="">— TBD —</option><option value="broker">Broker</option><option value="own_fleet">Own fleet</option>
            </select></label>
        </div>
        <div className="text-[13px] text-[#68665E]">{c.from_location} → {c.to_location}{c.cargo ? ` · ${c.cargo}` : ""}</div>

        <div className="border border-[#E5E3DB] rounded p-3 space-y-2">
          <div className="text-xs font-semibold text-[#1A1A18]">Deal outcome</div>
          <div className="flex items-center gap-2">
            <button onClick={() => patch({ outcome: "open", lost_reason: "" })} className={`flex-1 py-1.5 text-xs rounded border transition-colors ${(c.outcome || "open") === "open" ? "border-[#1A1A18] bg-[#1A1A18] text-white" : "border-[#E5E3DB] text-[#68665E] hover:border-[#1A1A18]"}`} data-testid="outcome-open">Open</button>
            <button onClick={() => patch({ outcome: "won", lost_reason: "" })} className={`flex-1 py-1.5 text-xs rounded border transition-colors ${c.outcome === "won" ? "border-[#2F6F4E] bg-[#2F6F4E] text-white" : "border-[#E5E3DB] text-[#68665E] hover:border-[#2F6F4E]"}`} data-testid="outcome-won">Won</button>
            <button onClick={() => setShowLost((v) => !v)} className={`flex-1 py-1.5 text-xs rounded border transition-colors ${c.outcome === "lost" ? "border-[#8A2526] bg-[#8A2526] text-white" : "border-[#E5E3DB] text-[#68665E] hover:border-[#8A2526]"}`} data-testid="outcome-lost">Lost</button>
          </div>
          {c.outcome === "lost" && c.lost_reason && <div className="text-[11px] text-[#8A2526]">Reason: {c.lost_reason}</div>}
          {showLost && (
            <div className="flex items-center gap-2">
              <input className="input-field !py-1.5 text-xs flex-1" placeholder="Reason lost (optional)" value={lostReason} onChange={(e) => setLostReason(e.target.value)} data-testid="lost-reason-input" />
              <button onClick={() => { patch({ outcome: "lost", lost_reason: lostReason }); setShowLost(false); }} className="btn-secondary !py-1.5 text-xs whitespace-nowrap" data-testid="confirm-lost">Mark Lost</button>
            </div>
          )}
        </div>

        <div className="border border-[#E5E3DB] rounded p-3 space-y-2">
          <div className="text-xs font-semibold text-[#1A1A18] flex items-center gap-1"><Link2 className="w-3.5 h-3.5" /> Linked records</div>
          <div className="flex flex-wrap gap-1">
            {c.quotation_number && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F7F1DE] text-[#8A6D1B]"><ScrollText className="w-3 h-3 inline mr-0.5" />{c.quotation_number}</span>}
            {(c.lr_numbers || []).map((n) => <span key={n} className="text-[10px] px-1.5 py-0.5 rounded bg-[#EAF0FB] text-[#2B5FB3]"><FileText className="w-3 h-3 inline mr-0.5" />{n}</span>)}
            {c.invoice_number && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#E3F0E7] text-[#2F6F4E]"><Receipt className="w-3 h-3 inline mr-0.5" />{c.invoice_number}</span>}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <input className="input-field !py-1.5 text-xs" placeholder="Link LR #" value={link.lr_number} onChange={(e) => setLink({ ...link, lr_number: e.target.value })} data-testid="link-lr" />
            <input className="input-field !py-1.5 text-xs" placeholder="Link Invoice #" value={link.invoice_number} onChange={(e) => setLink({ ...link, invoice_number: e.target.value })} data-testid="link-invoice" />
            <input className="input-field !py-1.5 text-xs" placeholder="Link Quote #" value={link.quotation_number} onChange={(e) => setLink({ ...link, quotation_number: e.target.value })} data-testid="link-quote" />
          </div>
          <button onClick={doLink} className="btn-secondary w-full !py-1.5 text-xs" data-testid="detail-link-btn">Link</button>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!c.original_pod_received} onChange={(e) => patch({ original_pod_received: e.target.checked })} data-testid="detail-original-pod" />
          <span className="text-[13px]">Original POD received (via courier)</span>
        </label>

        <label className="block"><span className="text-xs text-[#68665E]">Notes</span>
          <textarea className="input-field !py-1.5 text-sm mt-1" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => notes !== c.notes && patch({ notes })} data-testid="detail-notes" /></label>

        <button onClick={remove} className="btn-ghost text-[#8A2526] text-sm" data-testid="detail-delete"><Trash2 className="w-4 h-4" /> Delete consignment</button>
      </div>
    </Modal>
  );
}

function Modal({ title, children, onClose, testId }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()} data-testid={testId}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-headings font-bold text-[#1A1A18]">{title}</h3>
          <button onClick={onClose} className="text-[#68665E] hover:text-[#C84B31]"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
